import React from "react";
import { FaRegClock, FaArrowUp } from "react-icons/fa";
import "./PlanSelector.css";
import {
  PLANS,
  BADGE_LABELS,
  planByPayload,
  toPlanPayload,
  CUSTOM_PLAN,
  isCustomPlan,
  longestPlan,
  money,
  moneyExact,
  pricePerMonth,
  savingVsMonthly,
  annualCostAtMonthlyRate,
  extendedExpiry,
  formatDate,
  formatShortDate,
  expiryStatus,
} from "./plans";

/**
 * The plan cards, in one of two modes.
 *
 *   mode="new"     a first purchase. Cards read as rates — "$16.58/mo" — so the
 *                  three terms are directly comparable, with the saving against
 *                  paying monthly underneath.
 *   mode="extend"  a renewal. The same three plans, but priced as the one-off
 *                  invoice they actually are and labelled with the expiry date
 *                  they buy, because "to 17 Sep 2027" is the thing a renewing
 *                  customer is deciding between.
 *
 * `value` / `onChange` carry the plan's persisted string (plans.js `value`), so
 * this drops into an existing form field without changing what gets stored.
 */
export default function PlanSelector({
  mode = "new",
  value,
  onChange,
  currentExpiry = null,
  // Opt-in: only the new-licence picker offers custom pricing. A renewal is an
  // extension of an existing term, so "tell us what you need" has no meaning there.
  allowCustom = false,
}) {
  const isExtend = mode === "extend";

  return (
    <>
      <div className="plan-selector">
        {PLANS.map((plan) => {
          const payload = toPlanPayload(plan);
          const isSelected = payload === value;
          const saving = savingVsMonthly(plan);
          const target = isExtend ? extendedExpiry(currentExpiry, plan) : null;

          // Badge copy is looked up, never written here, so a badge can move
          // between plans in config without touching this component.
          const badge = plan.badge ? BADGE_LABELS[plan.badge] : null;

          return (
            <button
              type="button"
              key={plan.id}
              className={`plan-card${isSelected ? " is-selected" : ""}`}
              aria-pressed={isSelected}
              onClick={() => onChange(payload)}
            >
              {badge && <span className="plan-card__badge">{badge}</span>}

              <div className="plan-card__name">{isExtend ? plan.extendLabel : plan.label}</div>

              {isExtend ? (
                <>
                  <div className="plan-card__price">{money(plan.priceUsd)}</div>
                  {target && <div className="plan-card__note">to {formatDate(target)}</div>}
                </>
              ) : (
                <>
                  <div className="plan-card__price">
                    {moneyExact(pricePerMonth(plan))}
                    <span className="plan-card__per">/mo</span>
                  </div>
                  {saving > 0 ? (
                    <div className="plan-card__note plan-card__note--save">Save {money(saving)}</div>
                  ) : (
                    // Reframes the cheapest-looking option as the dearest one.
                    <div className="plan-card__note">
                      {money(annualCostAtMonthlyRate())} a year at this rate
                    </div>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      {allowCustom && (
        <button
          type="button"
          className={`plan-custom${isCustomPlan(value) ? " is-selected" : ""}`}
          aria-pressed={isCustomPlan(value)}
          onClick={() => onChange(CUSTOM_PLAN.payload)}
        >
          {/* <span className="plan-custom__title">{CUSTOM_PLAN.label}{" "}</span> */}
          <span className="plan-custom__note">
            Buying for a team, or need a custom term? Tell us and we'll quote it.
          </span>
        </button>
      )}
    </>
  );
}

/**
 * "You are nearly out of time" banner for the renewal flow. Renders nothing
 * without an expiry date, so it is safe to mount before a license is chosen.
 */
export function ExpiryNotice({ expiresAt }) {
  const status = expiryStatus(expiresAt);
  if (!status) return null;

  const { tone, days, date } = status;

  // Invoicing and payment are not instant, so the useful deadline is a couple
  // of days BEFORE expiry — that is the number a customer can act on.
  const actBy = new Date(date);
  actBy.setDate(actBy.getDate() - 2);

  const title =
    tone === "expired"
      ? `Expired ${days === 0 ? "today" : `${days} day${days === 1 ? "" : "s"} ago`}, on ${formatShortDate(date)}`
      : `Expires in ${days} day${days === 1 ? "" : "s"}, on ${formatShortDate(date)}`;

  const body =
    tone === "expired"
      ? "Your drawings stay yours, but SPCAD tools have stopped working. Renewing restores them as soon as payment clears."
      : `Your drawings stay yours, but SPCAD tools stop working. Invoicing and payment take 1–2 days, so request before ${formatShortDate(actBy)} to avoid a gap.`;

  return (
    <div className={`plan-expiry plan-expiry--${tone}`}>
      <FaRegClock style={{ marginTop: 3, flexShrink: 0 }} />
      <div>
        <div className="plan-expiry__title">{title}</div>
        <div>{body}</div>
      </div>
    </div>
  );
}

/**
 * Nudge toward the longest term. Shown only when a shorter one is selected and
 * only in the renewal flow, where "skip this form N times" is a concrete
 * promise about work the customer has just done and would otherwise repeat.
 */
export function PlanUpsell({ value, currentExpiry }) {
  const selected = planByPayload(value);
  const longest = longestPlan();
  if (!selected || selected.id === longest.id) return null;

  const extra = longest.priceUsd - selected.priceUsd;
  // How many renewals of the selected term the long plan replaces.
  const skipped = Math.round(longest.months / selected.months) - 1;
  const lockedTo = extendedExpiry(currentExpiry, longest);

  return (
    <div className="plan-upsell">
      <FaArrowUp style={{ marginTop: 3, flexShrink: 0 }} />
      <div>
        Go {longest.extendLabel} for {money(extra)} more and skip this form{" "}
        {skipped === 1 ? "once" : `${skipped} times`}.
        {lockedTo && ` Rate locked to ${new Date(lockedTo).getFullYear()}.`}
      </div>
    </div>
  );
}
